import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { AuthState } from '../../../auth/store/auth.model';
import { selectUser } from '../../../auth/store/auth.selector';
import { ProfileApiService } from '../../../pages/profile/profile-api.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-work-experience-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './work-experience-details.component.html',
  styleUrl: './work-experience-details.component.css'
})
export class WorkExperienceDetailsComponent implements OnInit {
  private readonly textMinLength = 2;
  private readonly textMaxLength = 100;
  private readonly descriptionMaxLength = 1000;
  readonly todayDate = new Date().toISOString().slice(0, 10);
  workExperienceForm: FormGroup;
  isSaving = false;
  openExperienceIndex = 0;
  userId: number | string | null = null;

  constructor(
    private fb: FormBuilder,
    private store: Store<AuthState>,
    private profileApiService: ProfileApiService,
    private toasterService: ToasterService
  ) {
    this.workExperienceForm = this.fb.group({
      experiences: this.fb.array([this.createExperienceForm()])
    });
  }

  ngOnInit(): void {
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        const masterUserId =
          localStorage.getItem('master_user_id') ??
          user?.id ??
          this.getUserIdFromStorage();

        if (masterUserId) {
          this.userId = masterUserId;
          this.loadWorkExperience(masterUserId);
        }
      }
    });
  }

  get experiences(): FormArray {
    return this.workExperienceForm.get('experiences') as FormArray;
  }

  createExperienceForm(data: any = {}): FormGroup {
    return this.fb.group(
      {
        id: [data.id ?? null],
        institute: [data.institute ?? '', this.requiredTextValidators],
        position: [data.position ?? '', this.requiredTextValidators],
        fromdate: [this.normalizeDate(data.fromdate) ?? '', this.dateValidators],
        todate: [this.normalizeDate(data.todate) ?? '', this.dateValidators],
        duration: [data.duration ?? ''],
        description: [data.description ?? '', [Validators.maxLength(this.descriptionMaxLength)]],
        is_active: [data.is_active ?? true]
      },
      { validators: this.dateRangeValidator() }
    );
  }

  loadWorkExperience(userId: number | string): void {
    this.profileApiService.getWorkExperienceByUserId(userId).subscribe({
      next: (res: any) => {
        const list = this.extractExperienceList(res);
        this.patchExperiences(list);
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Unable to load work experience details');
      }
    });
  }

  private extractExperienceList(res: any): any[] {
    if (Array.isArray(res?.data?.experience)) {
      return res.data.experience;
    }
    if (Array.isArray(res?.data?.experiences)) {
      return res.data.experiences;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    if (Array.isArray(res?.experience)) {
      return res.experience;
    }
    if (Array.isArray(res)) {
      return res;
    }
    return [];
  }

  private patchExperiences(items: any[]): void {
    this.experiences.clear();

    if (items.length) {
      items.forEach((item) => {
        this.experiences.push(this.createExperienceForm(item));
      });
      this.openExperienceIndex = 0;
      return;
    }

    this.experiences.push(this.createExperienceForm());
    this.openExperienceIndex = 0;
  }

  addExperience(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.experiences.push(this.createExperienceForm());
    this.openExperienceIndex = this.experiences.length - 1;
  }

  removeExperience(index: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.experiences.removeAt(index);
    this.experiences.updateValueAndValidity();
  }

  toggleExperience(index: number, event: Event): void {
    event.preventDefault();
    this.openExperienceIndex = this.openExperienceIndex === index ? -1 : index;
  }

  calculateDuration(index: number): void {
    const expGroup = this.experiences.at(index) as FormGroup;
    const from = expGroup.get('fromdate')?.value;
    const to = expGroup.get('todate')?.value;

    if (from && to) {
      const fromDate = new Date(from);
      const toDate = new Date(to);

      if (toDate < fromDate) {
        expGroup.patchValue({ duration: '' });
        return;
      }

      const totalMonths =
        (toDate.getFullYear() - fromDate.getFullYear()) * 12 +
        (toDate.getMonth() - fromDate.getMonth()) +
        (toDate.getDate() >= fromDate.getDate() ? 0 : -1);

      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;
      const parts: string[] = [];

      if (years > 0) {
        parts.push(`${years} Year${years > 1 ? 's' : ''}`);
      }
      if (months > 0) {
        parts.push(`${months} Month${months > 1 ? 's' : ''}`);
      }

      expGroup.patchValue({
        duration: parts.length ? parts.join(' ') : '0 Months'
      });
    }
  }

  saveWorkExperience(): void {
    if (this.workExperienceForm.invalid) {
      this.workExperienceForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Validation Error', this.getValidationErrorMessage());
      return;
    }

    if (!this.userId) {
      this.toasterService.setToaster('error', 'Error', 'User ID not found.');
      return;
    }

    this.isSaving = true;

    const payload = this.buildWorkExperiencePayload();

    this.profileApiService.createUserWorkExperience(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res?.status === 'success' || res?.code === 200) {
          this.toasterService.setToaster(
            'success',
            'Success',
            res?.message ?? 'Work experience saved successfully'
          );
          this.loadWorkExperience(this.userId!);
        } else {
          this.toasterService.setToaster(
            'error',
            'Error',
            res?.message ?? 'Failed to save work experience'
          );
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toasterService.setToaster(
          'error',
          'Failed',
          err?.error?.message || err?.message || 'Unable to save work experience'
        );
      }
    });
  }

  private buildWorkExperiencePayload(): any[] {
    const rows = this.workExperienceForm.getRawValue().experiences as any[];

    return rows
      .map((row, index) => ({ row, index }))
      .filter(
        ({ row }) =>
          row?.institute ||
          row?.position ||
          row?.fromdate ||
          row?.todate ||
          row?.description
      )
      .map(({ row, index }) => {
        const item: Record<string, unknown> = {
          user_id: this.userId,
          institute: row.institute ?? '',
          position: row.position ?? '',
          duration: row.duration ?? '',
          exp_type: `Experience ${index + 1}`,
          fromdate: row.fromdate ?? '',
          todate: row.todate ?? '',
          description: row.description ?? '',
          is_active: row.is_active ?? true
        };

        if (row.id) {
          item['id'] = row.id;
        }

        return item;
      });
  }

  showError(control: AbstractControl | null): boolean {
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  showDateRangeError(experience: AbstractControl): boolean {
    return !!(experience.hasError('dateRange') && (experience.dirty || experience.touched));
  }

  getTextError(control: AbstractControl | null, label: string): string {
    if (!control) {
      return '';
    }

    if (control.hasError('required')) {
      return `${label} is required`;
    }

    if (control.hasError('minlength')) {
      return `${label} must be at least ${control.getError('minlength')?.requiredLength} characters`;
    }

    if (control.hasError('maxlength')) {
      return `${label} cannot exceed ${control.getError('maxlength')?.requiredLength} characters`;
    }

    return '';
  }

  getDateError(control: AbstractControl | null, label: string): string {
    if (!control) {
      return '';
    }

    if (control.hasError('required')) {
      return `${label} is required`;
    }

    if (control.hasError('futureDate')) {
      return `${label} must be before or in current date`;
    }

    return '';
  }

  private get requiredTextValidators() {
    return [
      Validators.required,
      Validators.minLength(this.textMinLength),
      Validators.maxLength(this.textMaxLength)
    ];
  }

  private get dateValidators() {
    return [Validators.required, this.notFutureDateValidator()];
  }

  private notFutureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      return String(control.value) > this.todayDate ? { futureDate: true } : null;
    };
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const from = control.get('fromdate')?.value;
      const to = control.get('todate')?.value;

      if (!from || !to) {
        return null;
      }

      return String(to) < String(from) ? { dateRange: true } : null;
    };
  }

  private getValidationErrorMessage(): string {
    for (const experience of this.experiences.controls) {
      for (const field of [
        { label: 'Institute', controlName: 'institute', type: 'text' },
        { label: 'Designation', controlName: 'position', type: 'text' },
        { label: 'From date', controlName: 'fromdate', type: 'date' },
        { label: 'To date', controlName: 'todate', type: 'date' },
        { label: 'Description', controlName: 'description', type: 'text' }
      ]) {
        const control = experience.get(field.controlName);
        if (control?.invalid) {
          return field.type === 'date'
            ? this.getDateError(control, field.label)
            : this.getTextError(control, field.label);
        }
      }

      if (experience.hasError('dateRange')) {
        return 'To date must be after or same as From date';
      }
    }

    return 'Please correct the highlighted work experience fields.';
  }

  private normalizeDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toISOString().slice(0, 10);
  }

  private getUserIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }
}
